'use client';

import { useQueryClient } from '@tanstack/react-query';

import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { userInternalError } from '@sola/shared-utils/errors/supporters.ts';

import { MUTATION_KEYS, QUERY_KEYS } from '../constants.ts';
import {
  preferredUsernameQuery,
  supporterAddressQuery,
  supporterContactsQuery,
} from '../queries.ts';
import {
  useReadyForQueries,
  useSupporterMutation,
  useSupporterQuery,
} from './base.ts';
import { useSupporterDetails } from './details.ts';

// TODO: Do we want to also expose loading or error states?
export const useSupporterAccount = () => {
  const { data: supporter, isLoading: supporterDataLoading } =
    useSupporterDetails();
  const { data: supporterAddress, isLoading: addressDataLoading } =
    useSupporterQuery(supporterAddressQuery);
  const { data: preferredUsername, isLoading: preferredUsernameLoading } =
    useSupporterQuery(preferredUsernameQuery);
  const { data: contacts } = useSupporterQuery(supporterContactsQuery);

  return {
    supporter,
    supporterAddress,
    preferredUsername,
    contacts,
    showSkeleton:
      supporterDataLoading || addressDataLoading || preferredUsernameLoading,
  };
};

type UsernameUpdateMutationVariables = {
  preferredUsername: string;
};

export const useSupporterPreferredUsernameUpdate = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  const { authUser, actions } = useAuth('authUser', 'actions');

  return useSupporterMutation<
    { success: true; preferredUsername: string },
    UserError,
    UsernameUpdateMutationVariables
  >({
    mutationKey: [
      MUTATION_KEYS.preferredUsernameUpdate,
      authUser?.authUsername,
    ],
    mutationFn: async ({ preferredUsername }) => {
      await actions.updateUsername(preferredUsername);
      const status = actions.getCurrentStatus();
      if (status !== AuthStatusEnum.Success) {
        const error = actions.getError();
        throw error ?? userInternalError();
      }

      return { success: true, preferredUsername };
    },
    onSuccess: (_result, { preferredUsername }) => {
      // Update React Query cache for supporterDetails TODO: Will this be handled in US153944?
      queryClient.setQueryData(
        [QUERY_KEYS.supporterPreferredUsername, globalId],
        preferredUsername,
      );
    },
  });
};
